// GENERATED from backend/services/billMemory/core — do not edit; run backend/scripts/sync-bill-memory-core.js
/**
 * Bill Memory — which inventory items a name could mean (design §8). Built once per bill.
 *
 * Candidates: active items whose dictionary-key words include every word of the line, minus any
 * item whose extra words change the product ("onion" never means "spring onion", "lemon" never
 * means "lemon juice"). Variant words do not exclude: "kaju" means Kaju Whole, 2pc and 4pc
 * (source spec, worked example C). Twins: two active items with the same dictionary key
 * ("Kaju" and "Cashew", or "(palak)" and "palak") — always a choice, never a green (D22). Pure.
 */
import { makeMatchName, stripItemCode, joinedName, soundKey } from './matchName';
import { dictionaryKey } from './dictionary';

/** Words that make a different product when an item has them and the line does not (singular forms). */
const PRODUCT_WORDS = new Set([
  'spring', 'powder', 'baby', 'sweet', 'paste', 'sauce', 'juice', 'pulp', 'puree', 'flake', 'flour', 'oil', 'seed',
  'essence', 'extract', 'concentrate', 'syrup', 'crush', 'jam', 'pickle', 'chip', 'ketchup', 'masala', 'marinade',
  'marination', 'gravy', 'dressing', 'mayonnaise', 'soup', 'ice', 'icecream', 'cream', 'milk', 'bread', 'cake',
  'cookie', 'biscuit', 'chutney', 'salsa', 'spread', 'butter', 'vinegar', 'drink', 'soda', 'water', 'wine', 'tea',
]);

/**
 * lineKeyOf("45-ROCKET", dict) → { matchName: "45 rocket", stripped: "rocket", joined: "rocket", key, words, groups, regional, viaJoined }.
 * { joined: true } lets a line's dictionary key fall back to joined spellings (Fix 11 part B); an item's
 * own key never does.
 */
function lineKeyOf(printed, dictIndex, opts) {
  const matchName = makeMatchName(printed);
  const stripped = stripItemCode(matchName, printed);
  const dk = dictionaryKey(printed, dictIndex || null, opts);
  return { matchName, stripped, joined: joinedName(stripped), key: dk.key, words: dk.words, groups: dk.groups, regional: dk.regional, viaJoined: !!dk.viaJoined };
}

/** items: [{ item_id, name, is_active, merged_into_id, unit_symbol }] */
function buildInventoryIndex(items, dictIndex, familyOf) {
  const all = new Map();
  for (const it of items || []) all.set(Number(it.item_id), it);
  const entries = new Map();
  const byStripped = new Map();
  const byKey = new Map();
  const byWord = new Map();
  const byJoined = new Map(); // the stripped name with no spaces → items (Fix 11 part B)
  const bySound = new Map(); // soundKey → items (I15)
  const push = (m, k, e) => { if (!m.has(k)) m.set(k, []); m.get(k).push(e); };
  for (const it of all.values()) {
    if (!it.is_active || it.merged_into_id) continue;
    const k = lineKeyOf(it.name, dictIndex);
    const e = {
      item_id: Number(it.item_id), name: it.name, unit_symbol: it.unit_symbol || null, category_name: it.category_name || null,
      tax_slab: it.tax_slab == null ? null : it.tax_slab, family: familyOf ? familyOf(it.unit_symbol) : null,
      matchName: k.matchName, stripped: k.stripped, key: k.key, words: new Set(k.words), groups: k.groups,
    };
    entries.set(e.item_id, e);
    if (!byStripped.has(e.stripped)) byStripped.set(e.stripped, []);
    byStripped.get(e.stripped).push(e);
    if (!byKey.has(e.key)) byKey.set(e.key, []);
    byKey.get(e.key).push(e);
    for (const w of e.words) {
      if (!byWord.has(w)) byWord.set(w, new Set());
      byWord.get(w).add(e);
    }
    push(byJoined, joinedName(e.stripped), e);
    const sk = soundKey(e.stripped);
    if (sk.length >= 4) push(bySound, sk, e);
  }
  const resolveItemId = (id) => {
    let x = all.get(Number(id));
    for (let hops = 0; x && x.merged_into_id && hops < 5; hops++) x = all.get(Number(x.merged_into_id));
    return x && x.is_active && !x.merged_into_id ? Number(x.item_id) : null;
  };
  return { entries, byStripped, byKey, byWord, byJoined, bySound, resolveItemId, get: (id) => entries.get(Number(id)) || null };
}

function exactItem(stripped, index) {
  const list = index.byStripped.get(stripped);
  return list && list.length ? list[0] : null;
}

/** The one item whose name, run together, is this joined form — none when two items share it (Fix 11 part B). */
function joinedItem(joined, index) {
  const list = (index.byJoined && index.byJoined.get(joined)) || [];
  return list.length === 1 ? list[0] : null;
}

function candidatesFor(lineWords, index) {
  const words = [...new Set(lineWords || [])].filter(Boolean);
  if (!words.length) return [];
  const sets = words.map((w) => index.byWord.get(w));
  if (sets.some((s) => !s)) return [];
  sets.sort((a, b) => a.size - b.size);
  const lineSet = new Set(words);
  const out = [];
  for (const e of sets[0]) {
    if (!sets.every((s) => s.has(e))) continue;
    let changesProduct = false;
    for (const w of e.words) if (!lineSet.has(w) && PRODUCT_WORDS.has(w)) { changesProduct = true; break; }
    if (!changesProduct) out.push(e);
  }
  return out;
}

function twinsOf(entry, index) {
  if (!entry) return [];
  return (index.byKey.get(entry.key) || []).filter((e) => e.item_id !== entry.item_id);
}

export { PRODUCT_WORDS, lineKeyOf, buildInventoryIndex, exactItem, joinedItem, candidatesFor, twinsOf };
